/**
 * usePerformanceDropdowns — open-state + trigger-anchored positioning for the
 * Performance Analytics "Compare vs." / "Timeframe" dropdowns (SE-5551) and
 * the DotPlot X/Y axis selectors (SE-5568).
 *
 * All four selectors are hosted by the `FixedPositionDropdownShell` atom, which
 * fixes the Android mispositioning of the old gluestack Menu by returning
 * static `position: absolute` geometry on the first open frame. This hook owns
 * the open-states and their `useDropdownPositioning` wiring, and exposes
 * pre-memoised `ShellDropdownWiring` objects for the view plus `close*`
 * callbacks the select handlers call to dismiss the panel on selection.
 * `panelMinWidth: 192` preserves the SE-5149 option-row min-width floor.
 * @module features/player-detail/hooks/usePerformanceDropdowns
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Platform } from "react-native";

import useDropdownPositioning from "@/components/atoms/FixedPositionDropdownShell/useDropdownPositioning";
import { METRIC_DROPDOWN_POSITIONING } from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

/**
 * Shared positioning params for both shell-hosted dropdowns. `panelHeight`
 * caps the panel so a long timeframe list (Career + many seasons) scrolls
 * inside the dropdown instead of overflowing the viewport or being clipped by
 * the panel's `overflow-hidden` — the options are wrapped in a ScrollView.
 */
const POSITIONING = {
  panelMinWidth: 192,
  panelOffset: 4,
  panelHeight: 280,
} as const;

/** Timeframe variant of {@link POSITIONING} — right-anchored (see below). */
const TIMEFRAME_POSITIONING = { ...POSITIONING, anchor: "right" } as const;

/** Return shape of {@link usePerformanceDropdowns}. */
export interface PerformanceDropdownsResult {
  /** Shell wiring for the compare dropdown. */
  readonly compareDropdown: ShellDropdownWiring;
  /** Shell wiring for the timeframe dropdown. */
  readonly timeframeDropdown: ShellDropdownWiring;
  /** Shell wiring for the DotPlot X-axis metric dropdown (SE-5568). */
  readonly xAxisDropdown: ShellDropdownWiring;
  /** Shell wiring for the DotPlot Y-axis metric dropdown (SE-5568). */
  readonly yAxisDropdown: ShellDropdownWiring;
  /** Dismiss the compare panel (called on option select). */
  readonly closeCompareDropdown: () => void;
  /** Dismiss the timeframe panel (called on option select). */
  readonly closeTimeframeDropdown: () => void;
  /** Dismiss the X-axis panel (called on option select). */
  readonly closeXAxisDropdown: () => void;
  /** Dismiss the Y-axis panel (called on option select). */
  readonly closeYAxisDropdown: () => void;
}

/**
 * Owns one shell dropdown's open-state + positioning wiring.
 * @param positioning - `useDropdownPositioning` params (minus `isOpen`).
 * @returns The memoised wiring, the close callback, and the state setter
 * (for the shared Escape-to-close effect).
 */
function useShellDropdown(
  positioning: Omit<
    Parameters<typeof useDropdownPositioning>[0],
    "isOpen"
  > = POSITIONING
): {
  readonly wiring: ShellDropdownWiring;
  readonly close: () => void;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
} {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen,
    ...positioning,
  });

  const wiring = useMemo<ShellDropdownWiring>(
    () => ({ isOpen, onToggle, triggerRef, panelGeometry, shouldShow }),
    [isOpen, onToggle, triggerRef, panelGeometry, shouldShow]
  );

  return { wiring, close, setIsOpen };
}

/**
 * Owns the compare/timeframe/X-axis/Y-axis dropdown open-state + positioning.
 * @returns Memoised shell wiring + close callbacks.
 */
export function usePerformanceDropdowns(): PerformanceDropdownsResult {
  const compare = useShellDropdown();
  // SE-5551: the timeframe selector sits in the right half of the header, so a
  // min-192 panel anchored to its LEFT edge overflows the right screen margin
  // (native has no viewport clamp). Anchor it to the RIGHT edge of the trigger
  // so the panel expands leftward and stays on-screen.
  const timeframe = useShellDropdown(TIMEFRAME_POSITIONING);
  // SE-5568: the DotPlot axis triggers are the compact py-1 pills, so they use
  // the metric-dropdown offsets shared with the other metric selectors.
  const xAxis = useShellDropdown(METRIC_DROPDOWN_POSITIONING);
  const yAxis = useShellDropdown(METRIC_DROPDOWN_POSITIONING);

  const setCompareOpen = compare.setIsOpen;
  const setTimeframeOpen = timeframe.setIsOpen;
  const setXAxisOpen = xAxis.setIsOpen;
  const setYAxisOpen = yAxis.setIsOpen;
  const anyOpen =
    compare.wiring.isOpen ||
    timeframe.wiring.isOpen ||
    xAxis.wiring.isOpen ||
    yAxis.wiring.isOpen;

  // SE-5551: the gluestack Menu closed on Escape natively; the shell has no
  // keyboard dismissal, so restore Escape-to-close (a11y + the SE-5149 e2e
  // uses it to dismiss between the two dropdowns). Web-only: native has no
  // hardware keyboard and no `document`. The web guard can't run in the native
  // jest env, so coverage is delegated to the SE-5149 Playwright e2e.
  /* istanbul ignore next -- web-only; covered by the SE-5149 e2e */
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }
    if (!anyOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCompareOpen(false);
        setTimeframeOpen(false);
        setXAxisOpen(false);
        setYAxisOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [anyOpen, setCompareOpen, setTimeframeOpen, setXAxisOpen, setYAxisOpen]);

  // Memoised so consumers can depend on the result object itself (the
  // Container's useCallback deps take `dropdowns`, not each member).
  return useMemo(
    () => ({
      compareDropdown: compare.wiring,
      timeframeDropdown: timeframe.wiring,
      xAxisDropdown: xAxis.wiring,
      yAxisDropdown: yAxis.wiring,
      closeCompareDropdown: compare.close,
      closeTimeframeDropdown: timeframe.close,
      closeXAxisDropdown: xAxis.close,
      closeYAxisDropdown: yAxis.close,
    }),
    [
      compare.wiring,
      compare.close,
      timeframe.wiring,
      timeframe.close,
      xAxis.wiring,
      xAxis.close,
      yAxis.wiring,
      yAxis.close,
    ]
  );
}
